import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Alert, Button, LoadingSpinner, ReportDateRangePicker } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { FiRefreshCw, FiFilter } from 'react-icons/fi';

import ContractSummaryCards       from './ContractSummaryCards';
import ContractWeeklyChart        from './ContractWeeklyChart';
import ContractMonthlyTrend       from './ContractMonthlyTrend';
import ContractDomesticOverseaChart from './ContractDomesticOverseaChart';
import ContractTopRevenueChart    from './ContractTopRevenueChart';
import ContractSalesInchargeChart  from './ContractSalesInchargeChart';
import ContractProvinceChart       from './ContractProvinceChart';
import ContractCountryChart        from './ContractCountryChart';
import ContractSalesRegionChart    from './ContractSalesRegionChart';
import ContractCategoryChart       from './ContractCategoryChart';
import ContractDetailedTable      from './ContractDetailedTable';
import ContractFilterSidebar      from './ContractFilterSidebar';
import ContractDrillDownPanel     from './ContractDrillDownPanel';
import UnreleasedListPanel        from './UnreleasedListPanel';
import ContractHandleSummary      from './ContractHandleSummary';
import ContractReleaseDelayCard   from './ContractReleaseDelayCard';

import {
  fetchContractSummary,
  fetchContractWeekly,
  fetchContractMonthlyTrend,
  fetchContractSalesUnit,
  fetchContractDomesticOversea,
  fetchContractTopRevenue,
  fetchContractSalesIncharge,
  fetchContractProvince,
  fetchContractCountry,
  fetchContractRegion,
  fetchContractCategory,
  fetchContractDetailed,
  fetchContractFilterOptions,
  resetError
} from 'redux/crm/dashboard/contractSlice';

const DEFAULT_START   = dayjs().startOf('year').format('YYYY-MM-DD');
const DEFAULT_END     = dayjs().format('YYYY-MM-DD');
const LOAD_TIMEOUT_MS = 30000;

const ContractDashboard = () => {
  const dispatch       = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [pageReady,       setPageReady]       = useState(false);
  const [timeoutOccurred, setTimeoutOccurred] = useState(false);

  const [dateRange, setDateRange] = useState({
    startDate: searchParams.get('start_date') || DEFAULT_START,
    endDate:   searchParams.get('end_date')   || DEFAULT_END
  });

  const [filters, setFilters] = useState({
    sales_units:     searchParams.get('sales_units')?.split(',').filter(Boolean).map(Number) || [],
    sales_incharges: searchParams.get('sales_incharges')?.split(',').filter(Boolean) || [],
    customers:       searchParams.get('customers')?.split(',').filter(Boolean).map(Number) || []
  });

  const [groupBy,      setGroupBy]      = useState(searchParams.get('group_by') || 'month');
  const [detailedPage, setDetailedPage] = useState(1);
  const [perPage,      setPerPage]      = useState(50);
  const [sidebarOpen,  setSidebarOpen]  = useState(true);
  const [drill,        setDrill]        = useState({ open: false, title: '', extraParams: {} });
  const [unreleasedOpen, setUnreleasedOpen] = useState(false);

  const handleDrillDown = useCallback((title, extraParams = {}) => {
    setDrill({ open: true, title, extraParams });
  }, []);

  const {
    summary, weekly, monthlyTrend, salesUnit, domesticOversea,
    topRevenue, salesIncharge, province, country, region, category,
    detailed, filterOptions
  } = useSelector(s => s.crm_dashboard_contracts);

  useEffect(() => {
    if (summary.data || summary.error) setPageReady(true);
  }, [summary.data, summary.error]);

  useEffect(() => {
    if (pageReady) return;
    const id = setTimeout(() => setTimeoutOccurred(true), LOAD_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [pageReady]);

  const buildParams = useCallback((extra = {}) => {
    const p = { start_date: dateRange.startDate, end_date: dateRange.endDate, ...extra };
    if (filters.sales_units.length)     p.sales_units     = filters.sales_units;
    if (filters.sales_incharges.length) p.sales_incharges = filters.sales_incharges;
    if (filters.customers.length)       p.customers       = filters.customers;
    return p;
  }, [dateRange, filters]);

  const baseParams = useMemo(() => buildParams(), [buildParams]);

  // Sync URL
  useEffect(() => {
    const p = { start_date: dateRange.startDate, end_date: dateRange.endDate, group_by: groupBy };
    if (filters.sales_units.length)     p.sales_units     = filters.sales_units.join(',');
    if (filters.sales_incharges.length) p.sales_incharges = filters.sales_incharges.join(',');
    if (filters.customers.length)       p.customers       = filters.customers.join(',');
    setSearchParams(p);
  }, [dateRange, filters, groupBy, setSearchParams]);

  // Fetch all on filter change
  useEffect(() => {
    dispatch(fetchContractSummary(baseParams));
    dispatch(fetchContractWeekly(baseParams));
    dispatch(fetchContractSalesUnit(baseParams));
    dispatch(fetchContractDomesticOversea(baseParams));
    dispatch(fetchContractTopRevenue(baseParams));
    dispatch(fetchContractSalesIncharge(baseParams));
    dispatch(fetchContractProvince(baseParams));
    dispatch(fetchContractCountry(baseParams));
    dispatch(fetchContractRegion(baseParams));
    dispatch(fetchContractCategory(baseParams));
  }, [dispatch, baseParams]);

  // Filter options are the selectable UNIVERSE, scoped by date only — never narrowed
  // by the user's own selection (else picking a sales unit hides all the others and
  // you must Reset to get them back). Fetching here (date-only) also avoids re-hitting
  // the endpoint on every Apply.
  useEffect(() => {
    dispatch(fetchContractFilterOptions({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate
    }));
  }, [dispatch, dateRange.startDate, dateRange.endDate]);

  // Monthly trend re-fires on groupBy change
  useEffect(() => {
    dispatch(fetchContractMonthlyTrend({ ...baseParams, group_by: groupBy }));
  }, [dispatch, baseParams, groupBy]);

  // Detailed re-fires on page/perPage change
  useEffect(() => {
    dispatch(fetchContractDetailed({
      ...baseParams,
      pagination: { page: detailedPage, per_page: perPage }
    }));
  }, [dispatch, baseParams, detailedPage, perPage]);

  const handleRefresh = useCallback(() => {
    // refresh: true tells the API to bypass its short-lived dashboard cache and
    // recompute, so the Refresh button always pulls fresh numbers.
    const rp = { ...baseParams, refresh: true };
    dispatch(resetError());
    dispatch(fetchContractSummary(rp));
    dispatch(fetchContractWeekly(rp));
    dispatch(fetchContractMonthlyTrend({ ...rp, group_by: groupBy }));
    dispatch(fetchContractSalesUnit(rp));
    dispatch(fetchContractDomesticOversea(rp));
    dispatch(fetchContractTopRevenue(rp));
    dispatch(fetchContractSalesIncharge(rp));
    dispatch(fetchContractProvince(rp));
    dispatch(fetchContractCountry(rp));
    dispatch(fetchContractRegion(rp));
    dispatch(fetchContractCategory(rp));
    dispatch(fetchContractFilterOptions({ start_date: dateRange.startDate, end_date: dateRange.endDate }));
    dispatch(fetchContractDetailed({ ...baseParams, pagination: { page: detailedPage, per_page: perPage } }));
  }, [dispatch, baseParams, groupBy, detailedPage, perPage, dateRange]);

  const handleApplyFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    setDetailedPage(1);
    // Keep the filter panel open so the user can keep adjusting/adding filters
    // without having to re-open it via the Filters button after every Apply.
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({ sales_units: [], sales_incharges: [], customers: [] });
    setDetailedPage(1);
  }, []);

  const activeFilterCount = (filters.sales_units.length + filters.sales_incharges.length + filters.customers.length);

  if (!pageReady) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-screen tw-bg-gray-50 tw-gap-4">
        <LoadingSpinner size="lg" message="Loading Contract Dashboard…" />
        {timeoutOccurred && (
          <Alert variant="warning" className="tw-max-w-sm">
            <p className="tw-font-semibold tw-text-sm tw-mb-2">Dashboard is taking longer than expected</p>
            <p className="tw-text-xs tw-text-gray-700 tw-mb-3">The dashboard is still loading. This may indicate a slow connection or a temporary server issue.</p>
            <Button variant="primary" size="sm" onClick={() => window.location.reload()}>Reload Page</Button>
          </Alert>
        )}
      </div>
    );
  }

  const anyError = summary.error || weekly.error;

  return (
    <div className="tw-min-h-screen tw-bg-slate-50">
      {/* ── Sticky header ── */}
      <div className="tw-sticky tw-z-40 tw-bg-white tw-border-b tw-border-gray-200 tw-shadow-sm" style={{ top: 'var(--bs-top-nav-height, 4.3125rem)' }}>
        <div className="tw-px-5 tw-py-3 tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
          <div className="tw-flex-1 tw-min-w-0">
            <h1 className="tw-text-lg tw-font-bold tw-text-gray-900">Contract Dashboard</h1>
            <p className="tw-text-xs tw-text-gray-400 tw-hidden md:tw-block">
              Contracts · Addendums · Revenue · Weight · Trend · by contract date
            </p>
          </div>

          <ReportDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={({ startDate, endDate }) => { setDateRange({ startDate, endDate }); setDetailedPage(1); }}
          />

          <Button onClick={() => setSidebarOpen(o => !o)} variant="secondary">
            <FiFilter size={14} />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-xl tw-bg-blue-600 tw-text-white tw-text-xs tw-font-bold">
                {activeFilterCount}
              </span>
            )}
          </Button>

          <Button
            onClick={handleRefresh}
            variant="ghost"
            className="tw-flex tw-items-center tw-gap-1.5 tw-text-gray-500 hover:tw-text-gray-800"
          >
            <FiRefreshCw size={14} /> Refresh
          </Button>
        </div>
      </div>

      {anyError && (
        <div className="tw-px-5 tw-pt-4">
          <Alert type="error" message="Some sections failed to load. Try refreshing." dismissible />
        </div>
      )}

      {/* ── Main layout ── */}
      <div className="tw-flex tw-gap-5 tw-px-5 tw-py-5">

        {/* Sidebar */}
        <div className={`tw-flex-shrink-0 tw-w-64 tw-transition-all tw-duration-300 ${sidebarOpen ? 'tw-block' : 'tw-hidden'}`}>
          <div className="tw-sticky" style={{ top: 'calc(var(--bs-top-nav-height, 4.3125rem) + 4.25rem)' }}>
            <ContractFilterSidebar
              filterOptions={filterOptions.data}
              loading={filterOptions.loading}
              filters={filters}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
            />
          </div>
        </div>

        {/* Content */}
        <div className="tw-flex-1 tw-min-w-0 tw-space-y-5">

          {/* 1. KPI Summary */}
          <ContractSummaryCards
            data={summary.data}
            loading={summary.loading}
            onDrillDown={handleDrillDown}
            onShowUnreleased={() => setUnreleasedOpen(true)}
          />

          {/* 2. Weekly comparison + Domestic/Oversea */}
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-5">
            <ContractWeeklyChart
              data={weekly.data}
              loading={weekly.loading}
              error={weekly.error}
              baseParams={baseParams}
            />
            <ContractDomesticOverseaChart
              data={domesticOversea.data}
              loading={domesticOversea.loading}
              error={domesticOversea.error}
              onDrillDown={handleDrillDown}
            />
          </div>

          {/* 3. Monthly trend (full width) */}
          <ContractMonthlyTrend
            data={monthlyTrend.data}
            loading={monthlyTrend.loading}
            error={monthlyTrend.error}
            groupBy={groupBy}
            onGroupByChange={(g) => { setGroupBy(g); }}
          />

          {/* 4. Sales Region / Unit / Detail Table */}
          <ContractSalesRegionChart
            data={region.data}
            loading={region.loading}
            error={region.error}
            baseParams={baseParams}
            unitData={salesUnit.data}
            unitLoading={salesUnit.loading}
          />

          {/* 6. Sales Incharge + Province */}
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-5">
            <ContractCountryChart
              data={country.data}
              loading={country.loading}
              error={country.error}
              baseParams={baseParams}
            />

            <ContractProvinceChart
              data={province.data}
              loading={province.loading}
              error={province.error}
              baseParams={baseParams}
            />
          </div>

          {/* 7. Sales Incharge */}
          <ContractSalesInchargeChart
            data={salesIncharge.data}
            loading={salesIncharge.loading}
            error={salesIncharge.error}
            baseParams={baseParams}
          />

          {/* 5. Top Revenue (full width) */}
          <ContractTopRevenueChart
            data={topRevenue.data}
            loading={topRevenue.loading}
            error={topRevenue.error}
          />

          {/* 8. Category Breakdown */}
          <ContractCategoryChart
            data={category.data}
            loading={category.loading}
            error={category.error}
            baseParams={baseParams}
          />

          {/* 9. Handle Summary + Release Delay */}
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-5 tw-gap-5">
            <div className="lg:tw-col-span-2">
              <ContractHandleSummary baseParams={baseParams} />
            </div>
            <div className="lg:tw-col-span-3">
              <ContractReleaseDelayCard baseParams={baseParams} />
            </div>
          </div>

          {/* 10. Detailed Table */}
          <ContractDrillDownPanel
            open={drill.open}
            title={drill.title}
            baseParams={baseParams}
            extraParams={drill.extraParams}
            onClose={() => setDrill(prev => ({ ...prev, open: false }))}
          />

          <UnreleasedListPanel
            open={unreleasedOpen}
            baseParams={baseParams}
            onClose={() => setUnreleasedOpen(false)}
          />

          <ContractDetailedTable
            data={detailed.data}
            pagination={detailed.pagination}
            totals={detailed.totals}
            loading={detailed.loading}
            error={detailed.error}
            baseParams={baseParams}
            onPageChange={(page) => setDetailedPage(page)}
            onPageSizeChange={(size) => { setPerPage(size); setDetailedPage(1); }}
          />
        </div>
      </div>
    </div>
  );
};

export default ContractDashboard;
